import { ReadonlyURLSearchParams, usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'

type UrlValidationProps = {
  /**
   * Current search params, `null` while they are not known yet (validation is skipped then).
   * Pass `useSearchParams()` to follow every URL change (needs a Suspense boundary on static
   * pages), or `useClientSearchParams()` to only read the URL once after hydration.
   */
  searchParams: ReadonlyURLSearchParams | URLSearchParams | null
  /**
   * Checks for location.pathname
   */
  pathCheckRules?: {
    /**
     * Test pathname
     * @param pathname current pathname
     * @returns if pathname is valid
     */
    test: (pathname: string) => boolean
    /**
     * Return valid pathname based on current pathname
     * @param pathname invalid pathname
     * @returns new pathname that will replace current URL
     */
    getValidPathname: (pathname: string) => string
    /**
     * Will be triggered on check failure
     */
    onFail?: () => void
    /**
     * Will be triggered after all checks have passed
     */
    onSuccess?: () => void
  }[]
  /**
   * Checks for search parameters
   */
  searchParamsCheckRules?: {
    /**
     * Search key
     */
    key: string
    /**
     * Test value
     * @param value current value
     * @returns if value is valid
     */
    testValue?: (value: string) => boolean
    /**
     * Will be set if some check fails
     */
    defaultValue?: string
    /**
     * Will be triggered on check failure
     */
    onFail?: () => void
    /**
     * Will be triggered after all checks have passed
     * @param value valid value of search parameter
     */
    onSuccess?: (value: string) => void
  }[]
}

/**
 * Will check if URL is valid and trigger events on success/failure
 */
export const useUrlValidation = ({
  searchParams,
  pathCheckRules,
  searchParamsCheckRules,
}: UrlValidationProps) => {
  const pathname = usePathname()
  const router = useRouter()

  const cachedRules = useRef({
    pathRules: pathCheckRules,
    searchRules: searchParamsCheckRules,
  })

  useEffect(() => {
    if (searchParams == null) return

    let validPathname = pathname
    let validSearchParams: ReadonlyURLSearchParams | URLSearchParams = searchParams
    let shouldReplace = false

    const { pathRules, searchRules } = cachedRules.current

    if (pathRules) {
      pathRules.forEach(({ test, getValidPathname, onFail, onSuccess }) => {
        if (!test(pathname)) {
          onFail?.()

          validPathname = getValidPathname(pathname)
          shouldReplace = true
        } else {
          onSuccess?.()
        }
      })
    }

    if (searchRules) {
      searchRules.forEach(({ key, testValue, defaultValue, onFail, onSuccess }) => {
        const param = searchParams.get(key)

        if (param === null || (testValue && !testValue(param))) {
          onFail?.()

          if (defaultValue) {
            shouldReplace = true

            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.set(key, defaultValue ?? '')

            validSearchParams = newSearchParams
          }
        } else {
          onSuccess?.(param)
        }
      })
    }

    if (shouldReplace) {
      router.replace(`${validPathname}?${validSearchParams.toString()}`)
    }
  }, [pathname, searchParams, router])
}
